import React, { useEffect, useState } from 'react'
import { Switch } from '@headlessui/react'
import classnames from 'classnames'
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { updateMeterServiceApi, updateOptiRateCreditPayeeApi } from '../../../../apis/apis'
import { CREDIT_PAYEE_LABELS, type CreditPayee } from '../../../../apis/types'
import Badge from '../../../../components/Badge'
import CopyableValue from '../../../../components/CopyableValue'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import { Button } from '../../../../components/Form/Button'
import InlineEditInput from '../../../../components/Form/InlineEditInput'
import { getInputStyles } from '../../../../components/Form/styles'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import { formatUTCDate } from '../../../../util/string'
import {
  FieldLabel,
  isoDateSchema,
  LastBilled,
  OptiRateStatus,
  formatServiceAddress,
  toIsoDateValue,
  VnemPendingChangeBadge,
  type ServiceAgreementWithAccountNumber,
} from './serviceAgreementDisplay'
import {
  isEndDateTodayOrPast,
  type ServiceAgreementCloseRequest,
} from './ServiceAgreementCloseModal'
import type { ServiceAgreementReactivateRequest } from './ServiceAgreementReactivateModal'
import PortaledTooltip from './PortaledTooltip'
import { STATUS_CHIP_POSSIBLE_VALUES } from './accountStatusUtils'
import StatusChipTooltipContent from './StatusChipTooltipContent'

interface ActiveToggleProps {
  record: ServiceAgreementWithAccountNumber
  disabled?: boolean
  onRequestClose: (request: ServiceAgreementCloseRequest) => void
  onRequestReactivate: (request: ServiceAgreementReactivateRequest) => void
  onRefresh: () => Promise<unknown>
}

/** Stable (non-nested) toggle so Headless UI keeps controlled state while the API runs. */
const ActiveToggle: React.FC<ActiveToggleProps> = ({
  record,
  disabled = false,
  onRequestClose,
  onRequestReactivate,
  onRefresh,
}) => {
  const dispatch = useAppDispatch()
  const [checked, setChecked] = useState(record.active === true)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    setChecked(record.active === true)
  }, [record.active, record.id])

  const isDisabled = disabled || isUpdating

  const handleChange = async (newValue: boolean) => {
    if (checked === newValue || isUpdating) return

    // Keep switch off until the reactivate modal confirms — parent owns the modal.
    if (newValue) {
      onRequestReactivate({
        meterServiceId: record.id,
        serviceAgreementId: record.serviceAgreementId,
        existingStartDate: toIsoDateValue(record.startDate),
        existingEndDate: toIsoDateValue(record.endDate),
      })
      return
    }

    const existingEndDate = toIsoDateValue(record.endDate)
    // End date already today/past — just deactivate; no close modal needed.
    if (isEndDateTodayOrPast(existingEndDate)) {
      setChecked(false)
      setIsUpdating(true)
      try {
        await updateMeterServiceApi(record.id, { active: false })
        await onRefresh()
      } catch (error) {
        setChecked(true)
        console.error('Active status update failed:', error)
        void dispatch(
          pushToast({
            type: 'error',
            message: 'Could not deactivate service agreement',
            description: error instanceof Error ? error.message : undefined,
          })
        )
      } finally {
        setIsUpdating(false)
      }
      return
    }

    // Keep switch on until the close modal confirms — parent owns the modal.
    onRequestClose({
      meterServiceId: record.id,
      serviceAgreementId: record.serviceAgreementId,
      trigger: 'deactivate',
      proposedEndDate: (() => {
        const d = new Date()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${d.getFullYear()}-${month}-${day}`
      })(),
      currentlyActive: true,
      existingEndDate,
    })
  }

  return (
    <Switch.Group as="div" className="flex items-center gap-2">
      <Switch.Label as="span" className="text-xs font-medium text-slate-600 dark:text-slate-300" passive>
        Active
      </Switch.Label>
      <Switch
        checked={checked}
        disabled={isDisabled}
        onChange={(next) => {
          void handleChange(next)
        }}
        className={classnames(
          'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2',
          checked ? 'bg-emerald-500 dark:bg-emerald-600' : 'bg-gray-200 dark:bg-gray-700',
          isDisabled && 'cursor-not-allowed opacity-60'
        )}
      >
        <span
          aria-hidden="true"
          className={classnames(
            checked ? 'translate-x-4' : 'translate-x-0',
            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
          )}
        />
      </Switch>
    </Switch.Group>
  )
}

/** Toggle with confirm modal for post-billing transition (superuser). */
const PostBillingTransitionToggle: React.FC<{
  record: ServiceAgreementWithAccountNumber
  onRefresh: () => Promise<unknown>
}> = ({ record, onRefresh }) => {
  const dispatch = useAppDispatch()
  const [checked, setChecked] = useState(record.postBillingTransition === true)
  const [pendingValue, setPendingValue] = useState<boolean | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    setChecked(record.postBillingTransition === true)
  }, [record.postBillingTransition, record.id])

  const requestChange = (next: boolean) => {
    if (checked === next || isUpdating || pendingValue != null) return
    setPendingValue(next)
  }

  const confirmChange = async () => {
    if (pendingValue == null || isUpdating) return
    const next = pendingValue
    setIsUpdating(true)
    setChecked(next)
    try {
      await updateMeterServiceApi(record.id, { postBillingTransition: next })
      await onRefresh()
      void dispatch(pushToast({ message: 'Post-billing transition updated' }))
      setPendingValue(null)
    } catch (error) {
      setChecked(!next)
      void dispatch(
        pushToast({
          type: 'error',
          message: 'Could not update post-billing transition',
          description: error instanceof Error ? error.message : undefined,
        })
      )
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <>
      <Switch.Group as="div" className="flex items-center gap-2">
        <Switch
          checked={checked}
          disabled={isUpdating || pendingValue != null}
          onChange={requestChange}
          className={classnames(
            checked ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-600',
            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60'
          )}
        >
          <span
            aria-hidden="true"
            className={classnames(
              checked ? 'translate-x-4' : 'translate-x-0',
              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
            )}
          />
        </Switch>
        <Switch.Label as="span" className="text-sm text-slate-800 dark:text-slate-100" passive>
          {checked ? 'Yes' : 'No'}
        </Switch.Label>
      </Switch.Group>

      <CustomContentModal
        isOpen={pendingValue != null}
        onClose={isUpdating ? () => undefined : () => setPendingValue(null)}
      >
        <div className="w-full max-w-md rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Update post-billing transition?
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Change post-billing transition from{' '}
            <span className="font-medium text-slate-700 dark:text-slate-200">
              {checked ? 'Yes' : 'No'}
            </span>{' '}
            to{' '}
            <span className="font-medium text-slate-700 dark:text-slate-200">
              {pendingValue ? 'Yes' : 'No'}
            </span>
            {record.serviceAgreementId ? ` for SA ${record.serviceAgreementId}` : ''}.
          </p>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              color="slate"
              disabled={isUpdating}
              onClick={() => setPendingValue(null)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={isUpdating} onClick={() => void confirmChange()}>
              {isUpdating ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </div>
      </CustomContentModal>
    </>
  )
}

const CreditPayeeEditor: React.FC<{
  record: ServiceAgreementWithAccountNumber
  onRefresh: () => Promise<unknown>
}> = ({ record, onRefresh }) => {
  const dispatch = useAppDispatch()
  const currentValue = record.optiRateDetails?.creditPayee ?? 'PROJECT_CO_REIMBURSE'
  const [selected, setSelected] = useState<CreditPayee>(currentValue)
  const [pendingValue, setPendingValue] = useState<CreditPayee | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    setSelected(record.optiRateDetails?.creditPayee ?? 'PROJECT_CO_REIMBURSE')
  }, [record.optiRateDetails?.creditPayee, record.id])

  const requestChange = (next: CreditPayee) => {
    if (selected === next || isUpdating || pendingValue != null) return
    setPendingValue(next)
  }

  const confirmChange = async () => {
    if (pendingValue == null || isUpdating) return
    const next = pendingValue
    setIsUpdating(true)
    setSelected(next)
    try {
      await updateOptiRateCreditPayeeApi(record.id, next)
      await onRefresh()
      void dispatch(pushToast({ message: 'Credit payee updated' }))
      setPendingValue(null)
    } catch (error) {
      setSelected(currentValue)
      void dispatch(
        pushToast({
          type: 'error',
          message: 'Could not update credit payee',
          description: error instanceof Error ? error.message : undefined,
        })
      )
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <span>Credit Payee:</span>
        <select
          aria-label="Credit Payee"
          className={classnames(getInputStyles({ disabled: isUpdating || pendingValue != null }), 'w-auto')}
          value={selected}
          disabled={isUpdating || pendingValue != null}
          onChange={(event) => requestChange(event.target.value as CreditPayee)}
        >
          {Object.entries(CREDIT_PAYEE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <CustomContentModal
        isOpen={pendingValue != null}
        onClose={isUpdating ? () => undefined : () => setPendingValue(null)}
      >
        <div className="w-full max-w-md rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Update credit payee?</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Change credit payee from{' '}
            <span className="font-medium text-slate-700 dark:text-slate-200">
              {CREDIT_PAYEE_LABELS[selected]}
            </span>{' '}
            to{' '}
            <span className="font-medium text-slate-700 dark:text-slate-200">
              {pendingValue != null ? CREDIT_PAYEE_LABELS[pendingValue] : ''}
            </span>
            {record.serviceAgreementId ? ` for SA ${record.serviceAgreementId}` : ''}.
          </p>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              color="slate"
              disabled={isUpdating}
              onClick={() => setPendingValue(null)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={isUpdating} onClick={() => void confirmChange()}>
              {isUpdating ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </div>
      </CustomContentModal>
    </>
  )
}

const linkButtonClassName =
  'inline-flex items-center gap-1 rounded px-0.5 py-0.5 text-sm text-indigo-600 underline decoration-indigo-600/40 underline-offset-2 hover:text-indigo-800 hover:decoration-indigo-800 dark:text-indigo-400 dark:decoration-indigo-400/40 dark:hover:text-indigo-300 dark:hover:decoration-indigo-300'

export interface ServiceAgreementDetailProps {
  record: ServiceAgreementWithAccountNumber
  allRows: ServiceAgreementWithAccountNumber[]
  utilityName: string
  canRw: boolean
  canRo: boolean
  canSuperuser: boolean
  ccaProgram?: string
  isCloseSubmitting: boolean
  onBack: () => void
  onRequestClose: (request: ServiceAgreementCloseRequest) => void
  onRequestReactivate: (request: ServiceAgreementReactivateRequest) => void
  onRefresh: () => Promise<unknown>
  onEnrollOptiRate: (meterServiceCuid: string, isReoptimization: boolean) => void
  onEdit: () => void
}

const ServiceAgreementDetail: React.FC<ServiceAgreementDetailProps> = ({
  record,
  allRows,
  utilityName,
  canRw,
  canRo,
  canSuperuser,
  ccaProgram,
  isCloseSubmitting,
  onBack,
  onRequestClose,
  onRequestReactivate,
  onRefresh,
  onEnrollOptiRate,
  onEdit,
}) => {
  const meterNumber = record.meter.meterNumber
  const addressLine = formatServiceAddress(record)
  const ccaApplies = ccaProgram === 'ENROLLED' || record.ccaTariff != null

  const formatDate = (value: string | Date | null | undefined): string => {
    if (value == null || value === '') return '—'
    return formatUTCDate(value) || '—'
  }

  const meterAccountTypeLabel =
    record.meterAccountType === 'GENERATOR'
      ? 'Generator'
      : record.meterAccountType === 'BENEFITTING'
        ? 'Benefitting'
        : '—'

  const programs = record.programs ?? []

  const renderActiveControl = () => {
    if (canRw) {
      return (
        <ActiveToggle
          record={record}
          disabled={isCloseSubmitting}
          onRequestClose={onRequestClose}
          onRequestReactivate={onRequestReactivate}
          onRefresh={onRefresh}
        />
      )
    }

    return (
      <span className="text-sm text-slate-700 dark:text-slate-200">
        {record.active ? 'Active' : 'Inactive'}
      </span>
    )
  }

  const renderOptiRateActions = () => {
    const buttons: React.ReactNode[] = []

    if (
      (record.optiRateDetails === undefined || record.optiRateDetails?.status === 'NOT_ENROLLED') &&
      record.active &&
      record.optiRateEligible === true &&
      (utilityName === 'PG&E' || utilityName === 'SCE') &&
      record.optiRateDetails?.optiRateSAId == null
    ) {
      buttons.push(
        <button
          key="enroll"
          type="button"
          className={linkButtonClassName}
          onClick={() => {
            onEnrollOptiRate(record.id, false)
          }}
        >
          Enroll OptiRate
        </button>
      )
    }

    if (
      record.optiRateDetails?.status === 'ENROLLED' &&
      record.active &&
      (utilityName === 'PG&E' || utilityName === 'SCE') &&
      !allRows.some(
        (sa) =>
          sa.optiRateDetails?.status === 'PENDING' &&
          sa.optiRateDetails?.originalSAId === record.optiRateDetails?.originalSAId
      )
    ) {
      buttons.push(
        <button
          key="reenroll"
          type="button"
          className={linkButtonClassName}
          onClick={() => {
            const originalSA = allRows.find(
              (sa) => sa.serviceAgreementId === record.optiRateDetails?.originalSAId
            )
            onEnrollOptiRate(originalSA?.id ?? record.id, true)
          }}
        >
          Re-enroll
        </button>
      )
    }

    if (record.meter.greenButtonEnrolled) {
      buttons.push(
        <Link
          key="tariff-model"
          to={`https://kingenergy.retool.com/app/tariff-modeling/?_environment=${
            window.location.hostname !== 'admin.kingenergy.com' ? 'staging' : 'production'
          }&tenantAccountNumber=${record.tenantAccountNumber}&meterNumber=${record.meter.meterNumber}`}
          target="_blank"
          className={linkButtonClassName}
        >
          Tariff model
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
        </Link>
      )
    }

    if (buttons.length === 0) return null
    return <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">{buttons}</div>
  }

  const renderSalesforceMeter = () => {
    if (!canRo) return null

    if (canRw) {
      return (
        <div>
          <FieldLabel>Salesforce meter</FieldLabel>
          <InlineEditInput
            name="salesforceMeterId"
            label="Salesforce Meter ID"
            hideLabel
            required
            initialValue={record.meter?.salesforceMeterId === 'N/A' ? '' : record.meter?.salesforceMeterId}
            displayValue={record.meter?.salesforceMeterName ?? undefined}
            handleSubmit={async (editedValue) => {
              await updateMeterServiceApi(record.id, { salesforceId: editedValue }).then(() => {
                void onRefresh()
              })
            }}
            validationSchema={Yup.string().matches(
              /^(?:[a-zA-Z0-9]{15}|[a-zA-Z0-9]{18})$/,
              'Must be 15 or 18 characters'
            )}
            className="text-sm"
            linkTemplate="https://kingenergy.lightning.force.com/lightning/r/meter_data__c/@@value@@/view"
          />
        </div>
      )
    }

    return (
      <div>
        <FieldLabel>Salesforce meter</FieldLabel>
        {record.meter.salesforceMeterId != null && record.meter.salesforceMeterId !== 'N/A' ? (
          <Link
            target="_blank"
            className="break-all text-sm underline dark:underline"
            to={`https://kingenergy.lightning.force.com/lightning/r/meter_data__c/${record.meter.salesforceMeterId}/view`}
            title={record.meter.salesforceMeterId}
          >
            {record.meter.salesforceMeterName || record.meter.salesforceMeterId}
          </Link>
        ) : (
          <span className="text-sm text-slate-500 dark:text-slate-400">Not set</span>
        )}
      </div>
    )
  }

  const renderPeriod = () => {
    if (canRw) {
      return (
        <div className="space-y-1">
          <FieldLabel>Period</FieldLabel>
          <div className="flex items-center gap-2">
            <span className="w-9 shrink-0 text-xs text-slate-500 dark:text-slate-400">Start</span>
            <InlineEditInput
              name="startDate"
              label="Start Date"
              hideLabel
              compact
              initialValue={record.startDate ? new Date(record.startDate).toISOString().split('T')[0] : ''}
              handleSubmit={async (editedValue) => {
                await updateMeterServiceApi(record.id, { startDate: editedValue || null }).then(() => {
                  void onRefresh()
                })
              }}
              validationSchema={isoDateSchema}
              className="min-w-0 flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="w-9 shrink-0 text-xs text-slate-500 dark:text-slate-400">End</span>
            <InlineEditInput
              name="endDate"
              label="End Date"
              hideLabel
              compact
              initialValue={record.endDate ? new Date(record.endDate).toISOString().split('T')[0] : ''}
              handleSubmit={async (editedValue) => {
                const nextEndDate = editedValue || null
                if (nextEndDate != null && isEndDateTodayOrPast(nextEndDate) && record.active) {
                  onRequestClose({
                    meterServiceId: record.id,
                    serviceAgreementId: record.serviceAgreementId,
                    trigger: 'endDate',
                    proposedEndDate: nextEndDate,
                    currentlyActive: true,
                    existingEndDate: toIsoDateValue(record.endDate),
                  })
                  return false
                }
                await updateMeterServiceApi(record.id, { endDate: nextEndDate }).then(() => {
                  void onRefresh()
                })
              }}
              validationSchema={isoDateSchema}
              className="min-w-0 flex-1 text-sm"
            />
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-1">
        <FieldLabel>Period</FieldLabel>
        <div className="flex items-baseline gap-2 text-sm leading-snug">
          <span className="w-9 shrink-0 text-xs text-slate-500 dark:text-slate-400">Start</span>
          <span>{(record.startDate !== undefined && formatUTCDate(record.startDate)) || '—'}</span>
        </div>
        <div className="flex items-baseline gap-2 text-sm leading-snug">
          <span className="w-9 shrink-0 text-xs text-slate-500 dark:text-slate-400">End</span>
          <span>{(record.endDate !== undefined && formatUTCDate(record.endDate)) || '—'}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded-md border border-indigo-600 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 dark:border-indigo-400 dark:text-indigo-400 dark:hover:bg-indigo-500/10"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Service Agreements
        </button>
        {canSuperuser ? (
          <Button type="button" size={2} onClick={onEdit}>
            Edit
          </Button>
        ) : null}
      </div>

      <div className="grid gap-x-3 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <FieldLabel>Service agreement ID</FieldLabel>
          {record.serviceAgreementId ? (
            <CopyableValue
              value={record.serviceAgreementId}
              label="service agreement ID"
              valueClassName="font-mono tracking-tight text-slate-700 dark:text-slate-200"
            />
          ) : (
            <span className="font-mono tracking-tight text-slate-400">—</span>
          )}
        </div>
        <div>
          <FieldLabel>Meter</FieldLabel>
          {meterNumber ? (
            <CopyableValue
              value={meterNumber}
              label="meter number"
              valueClassName="font-mono tracking-tight text-slate-700 dark:text-slate-200"
            />
          ) : (
            <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
          )}
        </div>
        <div className="min-w-0">
          <FieldLabel>Address</FieldLabel>
          {addressLine ? (
            <CopyableValue
              value={addressLine}
              label="service address"
              mono={false}
              className="max-w-full"
              valueClassName="text-sm leading-snug text-slate-600 dark:text-slate-300"
            />
          ) : (
            <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
          )}
        </div>
        <div>
          <FieldLabel>Status</FieldLabel>
          {renderActiveControl()}
        </div>
        <div>
          <FieldLabel>Tariff</FieldLabel>
          {record.tariff?.schedule ? (
            <Badge status="UTILITY" displayLabel={record.tariff.schedule} variant="soft" />
          ) : (
            <div className="text-sm text-slate-500 dark:text-slate-400">—</div>
          )}
          {record.tariff?.name != null &&
          record.tariff.name !== '' &&
          record.tariff.name !== record.tariff.schedule ? (
            <div className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{record.tariff.name}</div>
          ) : null}
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Effective {formatDate(record.tariffEffectiveDate)} – {formatDate(record.tariffEndDate)}
          </div>
        </div>
        {ccaApplies ? (
          <div>
            <FieldLabel>CCA tariff</FieldLabel>
            {record.ccaTariff?.schedule ? (
              <Badge
                status="CCA"
                displayLabel={
                  record.ccaTariff.utilityShortName
                    ? `${record.ccaTariff.utilityShortName} · ${record.ccaTariff.schedule}`
                    : record.ccaTariff.schedule
                }
                variant="soft"
              />
            ) : (
              <div className="text-sm text-slate-500 dark:text-slate-400">—</div>
            )}
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              <div>
                Effective {formatDate(record.ccaTariffEffectiveDate)} –{' '}
                {formatDate(record.ccaTariffEndDate)}
              </div>
              {record.ccaVintageYear ? <div>Vintage {record.ccaVintageYear}</div> : null}
            </div>
          </div>
        ) : null}
        <div>
          <FieldLabel>Meter account type</FieldLabel>
          {record.meterAccountType === 'GENERATOR' || record.meterAccountType === 'BENEFITTING' ? (
            <Badge
              status={record.meterAccountType}
              displayLabel={meterAccountTypeLabel}
              variant="soft"
            />
          ) : (
            <div className="text-sm text-slate-500 dark:text-slate-400">—</div>
          )}
        </div>
        <div>
          <FieldLabel>Post-billing transition</FieldLabel>
          {canSuperuser ? (
            <PostBillingTransitionToggle record={record} onRefresh={onRefresh} />
          ) : (
            <div className="text-sm font-medium">{record.postBillingTransition ? 'Yes' : 'No'}</div>
          )}
        </div>
        {renderPeriod()}
        <div>
          <FieldLabel>Last billed</FieldLabel>
          <LastBilled record={record} />
        </div>
        <div>
          <FieldLabel>VNEM</FieldLabel>
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            {record.vnemStatus != null ? (
              <PortaledTooltip
                content={
                  <StatusChipTooltipContent
                    label="VNEM Status"
                    values={[...STATUS_CHIP_POSSIBLE_VALUES.vnemStatus]}
                    currentStatus={record.vnemStatus}
                  />
                }
                className="inline-flex w-fit shrink-0"
              >
                <Badge status={record.vnemStatus} variant="soft" />
              </PortaledTooltip>
            ) : (
              <div className="text-sm text-slate-500 dark:text-slate-400">—</div>
            )}
            {record.vnemPendingChange != null ? (
              <VnemPendingChangeBadge change={record.vnemPendingChange} />
            ) : null}
            {programs.length > 0 ? (
              programs.map((program) => (
                <Link
                  key={program.id}
                  to={`/program/programs/${program.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-w-0 max-w-full items-center gap-1 text-sm text-slate-800 underline transition-colors hover:text-indigo-700 dark:text-slate-100 dark:hover:text-indigo-300"
                >
                  <span className="truncate">{program.name || program.id}</span>
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
                </Link>
              ))
            ) : record.vnemStatus == null ? null : (
              <div className="text-xs text-slate-500 dark:text-slate-400">No linked program</div>
            )}
          </div>
        </div>
        {renderSalesforceMeter()}
        <div>
          <FieldLabel>OptiRate</FieldLabel>
          <OptiRateStatus
            record={record}
            allRows={allRows}
            detailed
            creditPayeeSlot={
              record.optiRateDetails?.status === 'PENDING' ? (
                <CreditPayeeEditor record={record} onRefresh={onRefresh} />
              ) : undefined
            }
          />
          {renderOptiRateActions()}
        </div>
      </div>
    </div>
  )
}

export default ServiceAgreementDetail
